import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import AdminIcon from '@mui/icons-material/AdminPanelSettingsRounded'
import CollapseIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded'
import ExpandIcon from '@mui/icons-material/Menu'
import caseIcon from '@assets/images/case-icon.svg'
import toolkitLogo from '/images/toolkit-logo-light.svg'
import { UserRole } from '@types'
import { useAppState } from '@hooks/app/app-state.hook'
import { useProjectWithSubscription } from '@hooks/project/project-with-subscription.hook'
import { useHasRoles } from '@hooks/user/has-roles.hook'
import {
  AdminButtonContainer,
  CollapseButtonContainer,
  Container,
  ExpandButtonContainer,
  FooterLeftContainer,
  HeaderComponentContainer,
  HeaderLeftContainer,
  HeaderRightContainer,
  ImgLogo,
  LeftContainer,
  MainContainer,
  PlanningFadeContainer,
  ProjectRowContainer,
  ProjectRowIcon,
  ProjectRowIconContainer,
  ProjectRowLabel,
  RightContainer,
  SideBarContainer,
} from './main.styled'

interface Props {
  SideBarComponent?: React.ReactNode
  SideBarFooterComponent?: React.ReactNode
  MainComponent?: React.ReactNode
  HeaderComponent?: React.ReactNode
  ActionsComponent?: React.ReactNode
}

export const MainLayout = (props: Props): JSX.Element => {
  const { SideBarComponent, SideBarFooterComponent, MainComponent, HeaderComponent, ActionsComponent } = props

  const { isSideBarMenuOpen, setSideBarMenuOpen, isZoomingPlanning } = useAppState()

  const { projectId } = useParams()

  const { hasRoles } = useHasRoles()

  const { t, i18n } = useTranslation()

  const sectionAdminPath = `/${i18n.language}/admin/users`

  const { pathname } = useLocation()

  const isAdminPath = pathname.startsWith(`/${i18n.language}/admin`)

  const hasSidebarAccessPermission = hasRoles({
    userRoles: [UserRole.RoleAdmin, UserRole.RoleUser],
  })

  useEffect(() => {
    if (!hasSidebarAccessPermission) {
      setSideBarMenuOpen(false)
    }
  }, [hasSidebarAccessPermission, setSideBarMenuOpen])

  const handleRightContainerClick = (e: React.MouseEvent) => {
    // Check if the click target is not the ExpandButton
    if (!e.currentTarget.closest('.expand-button')) {
      setSideBarMenuOpen(false)
    }
  }

  const handleExpandButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent event from bubbling up to RightContainer
    setSideBarMenuOpen(!isSideBarMenuOpen)
  }

  return (
    <Container>
      <LeftContainer isOpen={isSideBarMenuOpen}>
        <HeaderLeftContainer>
          <ImgLogo src={toolkitLogo} />
          <CollapseButtonContainer className="collapse-button" onClick={() => setSideBarMenuOpen(false)}>
            <CollapseIcon />
          </CollapseButtonContainer>
        </HeaderLeftContainer>
        <SideBarContainer>{SideBarComponent}</SideBarContainer>
        <FooterLeftContainer>{SideBarFooterComponent}</FooterLeftContainer>
      </LeftContainer>
      <RightContainer onClick={handleRightContainerClick}>
        <HeaderRightContainer>
          {hasSidebarAccessPermission && (
            <ExpandButtonContainer
              className="expand-button"
              isOpen={isSideBarMenuOpen}
              onClick={handleExpandButtonClick}
            >
              <ExpandIcon />
            </ExpandButtonContainer>
          )}

          {projectId && <ProjectName projectId={projectId} />}

          <PlanningFadeContainer
            isAdminPath={isAdminPath}
            style={{ opacity: isZoomingPlanning && !isAdminPath ? 1 : 0 }}
          >
            {t('layouts_main_planning_label')}
          </PlanningFadeContainer>

          {ActionsComponent && ActionsComponent}

          {HeaderComponent && <HeaderComponentContainer>{HeaderComponent}</HeaderComponentContainer>}

          {hasRoles({ userRoles: [UserRole.RoleAdmin] }) && (
            <AdminButtonContainer to={sectionAdminPath}>
              <AdminIcon />
            </AdminButtonContainer>
          )}
        </HeaderRightContainer>
        <MainContainer>{MainComponent}</MainContainer>
      </RightContainer>
    </Container>
  )
}

interface ProjectNameProps {
  projectId: string
}

const ProjectName = (props: ProjectNameProps) => {
  const { projectId } = props

  const { data } = useProjectWithSubscription(projectId)

  return data?.project ? (
    <ProjectRowContainer>
      <ProjectRowIconContainer>
        <ProjectRowIcon src={caseIcon} />
      </ProjectRowIconContainer>
      <ProjectRowLabel>{data.project.name}</ProjectRowLabel>
    </ProjectRowContainer>
  ) : null
}
